import { Card } from "@imminently/react/ui/core/card";
import { Text } from "@imminently/react/ui/core/text";
import { Button, Collapse, Divider, Stack, Tooltip, Box } from "@mui/material";
import { startCase } from "lodash";
import moment from "moment";
import { PropsWithChildren, useState } from "react";

import { CalendartimeIcon } from "@/assets/icons";
import { ActionIcon } from "@/components/Action";
import ATSScore from "@/components/ATSScore";
import { Icon } from "@/components/Icon";
import ObservationSummary from "@/components/ObservationSummary/ObservationSummary";
import PatientTaskButton from "@/components/PatientTaskButton";
import { formatName, getActiveTasks, getDOB, getSex, makeDOBString } from "@/global";
import { ATS_COLOURS, FRAILTY_LABELS, PAIN_LABELS } from "@/resources/constants";
import { Frailty, PatientEncounter, Task } from "@/resources/types";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import PatientName from "./Patient/PatientName";

const styles = {
  root: {
    width: 320,
    height: "fit-content",
    padding: "1rem 1.2rem",
  },
  expandableRow: {
    justifyContent: "space-between",
    cursor: "pointer",
  },
  smallText: {
    fontSize: "0.9rem",

    "& .MuiTypography-root": {
      fontSize: "0.9rem",
    },
  },
  task: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "0.5rem",
    padding: "0.25rem",
    minWidth: "2rem",
  },
};

enum Screen {
  MAIN,
  NOTES,
}

type ObservationNote = {
  date: string;
  note: string;
};

const RowDivider = () => <Divider sx={{ my: 1 }} />;

const ActionTooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "primary.25",
      color: "primary.900",
      border: "1px solid",
      borderColor: "primary.100",
    },
  },
  arrow: {
    sx: {
      color: "primary.25",
      border: "1px solid",
      borderColor: "primary.100",
    },
  },
};

type InfoRowProps = PropsWithChildren & {
  label: string;
  expandable?: boolean; // Make the row clickable to view content
};

// Render a row on the main screen of the card such as Pain, Location, ATS, etc.
const InfoRow = (props: InfoRowProps) => {
  const { label, expandable, children } = props;
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded((oldExpanded) => !oldExpanded);

  if (!expandable || !children) {
    return (
      <Stack direction="row" justifyContent="space-between">
        <Text bold>{label}</Text>
        {children || <Text color="primary.300">-</Text>}
      </Stack>
    );
  }

  return (
    <Stack>
      <Stack direction="row" sx={styles.expandableRow} onClick={toggleExpanded}>
        <Text bold>{label}</Text>
        <Icon icon={expanded ? "chevronup" : "chevrondown"} />
      </Stack>
      <Collapse in={expanded}>
        <Stack mt={1.5}>{children}</Stack>
      </Collapse>
    </Stack>
  );
};

type NoteProps = {
  note: ObservationNote;
  padding?: number; // Vertical padding
};

const Note = ({ note, padding }: NoteProps) => {
  return (
    <Stack sx={styles.smallText} gap={0.5} py={padding}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <CalendartimeIcon width={16} height={16} />
        <Text>{note.date}</Text>
      </Stack>
      <Text>{note.note}</Text>
    </Stack>
  );
};

type MainScreenProps = {
  encounter: PatientEncounter;
  ats?: number;
  notes?: ObservationNote[];
  onScreenChange: (screen: Screen) => void;
};

const MainScreen = (props: MainScreenProps) => {
  const { encounter, ats, notes, onScreenChange } = props;
  const { frailty, location, observations, pain } = encounter;
  const validObservations = (observations || []).filter((obs) => !obs.invalid);
  const tasks = encounter.tasks && getActiveTasks(encounter.tasks);
  const { settings } = useSettingsContext();
  
  // Get task definitions from settings
  const taskDefinitions = (settings as any)?.taskDefinitions || [];

  // Put Patient problem first
  // TODO `encounter.problem` can't be undefined according to the
  // PatientEnctouner type, but for some encounters it is. Please review.
  const patientFirst = (a: [string, any], b: [string, any]) => (
    a[0] === "patient" ? -1 : b[0] === "patient" ? 1 : 0
  );

  const problems = encounter.problem
    ? Object.entries(encounter.problem).sort(patientFirst)
    : [];
  return (
    <>
      <RowDivider />

      <InfoRow label="ATS">{ats && <ATSScore score={ats} />}</InfoRow>

      <RowDivider />

      <InfoRow label="Pain">
        {pain !== undefined &&
          <Text>{pain} - {PAIN_LABELS[pain]}</Text>
        }
      </InfoRow>
      <InfoRow label="Frailty">
        {frailty !== undefined && (
          frailty === Frailty.NOT_SURE
            ? <Text color="primary.300">{FRAILTY_LABELS[frailty]}</Text>
            : <Text>{frailty} - {FRAILTY_LABELS[frailty]}</Text>
        )}
      </InfoRow>

      <RowDivider />

      <InfoRow label="Location">
        {location && <Text>{location.name}</Text>}
      </InfoRow>

      <RowDivider />

      <InfoRow label="Tasks">
        {tasks && tasks.length > 0 && (() => {
          // Card width is 320px, padding is 1.2rem (19.2px) on each side
          // So available width is approximately 320 - 38.4 = ~281px
          // Using a conservative estimate to account for label and other spacing
          const availableWidth = 280;
          
          // Task buttons with small=true have min-width: 2.5rem (40px) + padding (0.5rem = 8px each side)
          // In practice, they're typically around 48-56px wide including padding and icon
          // Gap between buttons is 3.2px (gap={0.4} = 0.4 * 8px = 3.2px)
          // So each task button effectively takes ~56px (48px button + 8px gap, using similar estimate as ActionsCell)
          const buttonWidthWithGap = 56;
          
          // Calculate how many buttons we can fit, starting conservatively
          let numVisible = 0;
          
          // Estimate "+X" button width based on number of hidden tasks
          const estimatePlusButtonWidth = (hiddenCount: number) => {
            // Base: 32px min-width + 8px padding on each side = 48px
            // Plus text width: roughly 10px per digit (more conservative)
            // Plus the "+" symbol: ~8px
            const textWidth = (String(hiddenCount).length + 1) * 10; // +1 for the "+" symbol
            return 48 + textWidth + 8; // 8px gap before button
          };
          
          // Add a small safety buffer to account for any rendering differences
          const safetyBuffer = 4;
          const adjustedWidth = availableWidth - safetyBuffer;
          
          // Calculate required width for all tasks
          const requiredWidth = tasks.length * buttonWidthWithGap;
          
          if (requiredWidth > adjustedWidth) {
            // Try to fit as many buttons as possible while leaving room for "+X"
            for (let i = 0; i < tasks.length; i++) {
              const spaceUsed = (i + 1) * buttonWidthWithGap;
              const hiddenCount = tasks.length - (i + 1);
              const plusButtonWidth = estimatePlusButtonWidth(hiddenCount);
              
              if (spaceUsed + plusButtonWidth <= adjustedWidth) {
                numVisible = i + 1;
              } else {
                break;
              }
            }
            
            // Ensure we show at least 0 buttons (just the "+X" button if card is very narrow)
            numVisible = Math.max(0, numVisible);
            
            const visibleTasks = tasks.slice(0, numVisible);
            const hiddenTasks = tasks.slice(numVisible);
            
            const tooltip = (
              <Stack direction="row" gap={1}>
                {hiddenTasks.map((task) => {
                  const taskDef = taskDefinitions.find((td: any) => td.name === task.type);
                  if (taskDef) {
                    return (
                      <PatientTaskButton
                        key={task.type}
                        value={task}
                        onClick={() => {}}
                        small={true}
                        taskDefinition={taskDef}
                        disabled={true}
                      />
                    );
                  }
                  return null;
                })}
              </Stack>
            );
            
            return (
              <Stack direction="row" gap={0.4}>
                {visibleTasks.map((task) => {
                  const taskDef = taskDefinitions.find((td: any) => td.name === task.type);
                  if (taskDef) {
                    return (
                      <PatientTaskButton
                        key={task.type}
                        value={task}
                        onClick={() => {}}
                        small={true}
                        taskDefinition={taskDef}
                        disabled={true}
                      />
                    );
                  }
                  return null;
                })}
                {hiddenTasks.length > 0 && (
                  <Tooltip
                    title={tooltip}
                    placement="top"
                    slotProps={ActionTooltipSlotProps}
                  >
                    <Box
                      sx={{
                        ...styles.task,
                        color: "white",
                        bgcolor: "primary.700",
                      }}
                    >
                      <Text variant="subtitle1" sx={{ lineHeight: 1 }}>
                        +{hiddenTasks.length}
                      </Text>
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            );
          }
          
          // All tasks fit, render them all
          return (
            <Stack direction="row" gap={0.4}>
              {tasks.map((task) => {
                const taskDef = taskDefinitions.find((td: any) => td.name === task.type);
                if (taskDef) {
                  return (
                    <PatientTaskButton
                      key={task.type}
                      value={task}
                      onClick={() => {}}
                      small={true}
                      taskDefinition={taskDef}
                      disabled={true}
                    />
                  );
                }
                return null;
              })}
            </Stack>
          );
        })()}
      </InfoRow>

      <RowDivider />

      <InfoRow label="Presenting Problem" expandable>
        {problems.length > 0 && (
          <Stack sx={styles.smallText} gap={2}>
            {problems.map(([probType, probText], index) => (
              <Stack key={index}>
                <Text bold>{startCase(probType)}</Text>
                <Text>{probText}</Text>
              </Stack>
            ))}
          </Stack>
        )}
      </InfoRow>

      <RowDivider />

      <InfoRow label="Last Medical Note" expandable>
        {notes && notes.length > 0 && (
          <>
            <Note note={notes[notes.length - 1]} />
            {notes.length > 1 && (
              <Button
                sx={{ ...styles.smallText, ml: "auto" }}
                variant="text"
                onClick={() => onScreenChange(Screen.NOTES)}
              >
                Show older notes
              </Button>
            )}
          </>
        )}
      </InfoRow>

      <RowDivider />

      <Stack gap={1}>
        {validObservations.length > 0 ? (
          <>
            <Text bold>Observations</Text>
            <ObservationSummary
              mini
              wrap
              align="center"
              observations={validObservations}
            />
          </>
        ) : (
          <InfoRow label="Observations" />
        )}
      </Stack>
    </>
  );
};

type NotesScreenProps = {
  encounter: PatientEncounter;
  notes?: ObservationNote[];
  onScreenChange: (screen: Screen) => void;
};

const NotesScreen = (props: NotesScreenProps) => {
  const { encounter, notes, onScreenChange } = props;
  const { observations } = encounter;

  if (!observations || !notes) return <Text>No notes</Text>;

  return (
    <>
      <Text sx={{ mt: 2 }} bold>
        Medical Notes
      </Text>

      {notes.map((note) => (
        <>
          <RowDivider />
          <Note note={note} padding={1} />
        </>
      ))}

      <Button
        sx={{ ...styles.smallText, mt: 0.5, width: "100%", py: 0.2 }}
        variant="outlined"
        size="small"
        onClick={() => onScreenChange(Screen.MAIN)}
      >
        Close Medical Notes
      </Button>
    </>
  );
};

type PatientCardProps = { 
  encounter: PatientEncounter; 
  overrideStyle?: any;
};

const getPersonalDetails = (encounter: PatientEncounter) => {
  const dobInfo = getDOB(encounter);
  const dob = dobInfo.fromPatient || dobInfo.fromDelegate;

  const dobText = dob ? makeDOBString(dob) : "N/A";

  const sexInfo = getSex(encounter);

  const sexText = sexInfo.fromPatient || sexInfo.fromDelegate || "U";

  return [
    sexText, // TODO this should exist on some resource
    dobText,
    encounter.patient.umrn,
  ];
};

export const PatientCard = ({ encounter, overrideStyle }: PatientCardProps) => {
  const [screen, setScreen] = useState(Screen.MAIN);
  const { observations } = encounter;

  if (!encounter.patient) {
    return null;
  }

  const { firstName, lastName } = encounter.patient;

  const personalDetails = getPersonalDetails(encounter).filter(Boolean).join(" • ");

  const ats = isNaN(Number(encounter.atsScore))
    ? undefined
    : Number(encounter.atsScore);

  const cardStyle = {
    ...styles.root,
    backgroundColor: ats ? ATS_COLOURS[ats - 1].backgroundBright : "grey.50",
    ...(overrideStyle || {})
  };

  const notes =
    observations &&
    observations
      .filter((obs) => !obs.invalid && obs.note !== undefined)
      .reverse()
      .map((obs) => ({
        date: moment(obs.lastModified).format("dddd D MMMM - HH:mm"),
        note: obs.note as string,
      }));

  return (
    <Card sx={cardStyle} elevation={0}>
      <Text bold><PatientName encounter={encounter} /></Text>
      <Text variant="caption" color="primary.300">
        {personalDetails}
      </Text>

      {screen === Screen.MAIN && (
        <MainScreen
          encounter={encounter}
          ats={ats}
          notes={notes}
          onScreenChange={(newScreen) => setScreen(newScreen)}
        />
      )}

      {screen === Screen.NOTES && (
        <NotesScreen
          encounter={encounter}
          notes={notes}
          onScreenChange={(newScreen) => setScreen(newScreen)}
        />
      )}
    </Card>
  );
};
